import React, { createContext, useContext, useReducer, useEffect, useState } from 'react';
import { formatTime } from '../utils/timeUtils';
import { EVENT_TYPES, SCORE_EVENTS, MATCH_PERIODS } from '../constants/rugbyConstants';
import { useAuth } from './AuthContext';
import { useDatabase } from './DatabaseContext';
import useTimer from '../hooks/useTimer'; // Import the useTimer hook

// Create context
const RugbyTrackerContext = createContext();

// Initial state
const initialState = {
  // Match state
  homeTeamName: 'Home',
  awayTeamName: 'Away',
  homeTeamScore: 0,
  awayTeamScore: 0,
  teamDirection: 'left-to-right',
  
  // We'll use useTimer for timer state - these remain just for type safety
  seconds: 0,
  isRunning: false,
  
  // Period state
  matchPeriod: MATCH_PERIODS[0],
  
  // Events and sanctions
  events: [],
  sanctions: [],
  eventSanctionMap: {},
  
  // UI state
  activeEventType: '',
  activeTeam: '',
  comment: '',
  
  // Current match ID (for Supabase integration)
  currentMatchId: null,
  
  // Persisted to Supabase flag
  isPersisted: false,
};

// Define reducers
function rugbyReducer(state, action) {
  switch (action.type) {
    // Match state actions
    case 'SET_HOME_TEAM_NAME':
      return { ...state, homeTeamName: action.payload };
    case 'SET_AWAY_TEAM_NAME':
      return { ...state, awayTeamName: action.payload };
    case 'SET_TEAM_DIRECTION':
      return { ...state, teamDirection: action.payload };
    case 'UPDATE_SCORES':
      return { 
        ...state, 
        homeTeamScore: action.payload.homeTeamScore,
        awayTeamScore: action.payload.awayTeamScore 
      };
    case 'SET_CURRENT_MATCH':
      return {
        ...state,
        currentMatchId: action.payload.id,
        homeTeamName: action.payload.homeTeamName,
        awayTeamName: action.payload.awayTeamName,
        homeTeamScore: action.payload.homeTeamScore || 0,
        awayTeamScore: action.payload.awayTeamScore || 0,
        events: action.payload.events || [],
        isPersisted: true
      };
      
    // Timer actions - now managed by useTimer, but kept for compatibility
    case 'SET_SECONDS':
      return { ...state, seconds: action.payload };
    case 'SET_TIMER_RUNNING':
      return { ...state, isRunning: action.payload };
      
    // Period actions
    case 'SET_MATCH_PERIOD':
      return { ...state, matchPeriod: action.payload };
      
    // Event actions
    case 'SET_EVENT_TYPE':
      return { ...state, activeEventType: action.payload };
    case 'SET_TEAM':
      return { ...state, activeTeam: action.payload };
    case 'SET_COMMENT':
      return { ...state, comment: action.payload };
    case 'ADD_EVENT':
      return { ...state, events: [...state.events, action.payload] };
    case 'DELETE_EVENT':
      return { 
        ...state, 
        events: state.events.filter(event => event.id !== action.payload) 
      };
    case 'EDIT_EVENT':
      return { 
        ...state, 
        events: state.events.map(event => 
          event.id === action.payload.id ? action.payload : event
        ) 
      };
    case 'CLEAR_EVENTS':
      return { ...state, events: [] };
      
    // Sanction actions
    case 'ADD_SANCTION':
      return {
        ...state,
        sanctions: [...state.sanctions, action.payload],
        eventSanctionMap: {
          ...state.eventSanctionMap,
          [action.payload.eventId]: action.payload.id
        }
      };
    case 'UPDATE_SANCTION':
      return {
        ...state,
        sanctions: state.sanctions.map(sanction =>
          sanction.id === action.payload.id ? action.payload : sanction
        )
      };
    case 'REMOVE_SANCTION':
      return {
        ...state,
        sanctions: state.sanctions.filter(sanction => sanction.id !== action.payload)
      };
    case 'COMPLETE_SANCTION':
      return {
        ...state,
        sanctions: state.sanctions.map(sanction =>
          sanction.id === action.payload ? { ...sanction, isActive: false } : sanction
        )
      };
    case 'CLEAR_SANCTIONS':
      return { ...state, sanctions: [], eventSanctionMap: {} };
      
    // Check and update sanctions
    case 'CHECK_SANCTIONS':
      const updatedSanctions = state.sanctions.map(sanction => {
        if (!sanction.isActive) return sanction;
        
        const elapsedSeconds = state.seconds - sanction.startTime;
        if (elapsedSeconds >= sanction.duration) {
          return { ...sanction, isActive: false };
        }
        return sanction;
      });
      
      if (JSON.stringify(updatedSanctions) !== JSON.stringify(state.sanctions)) {
        return { ...state, sanctions: updatedSanctions };
      }
      return state;
      
    // Reset all
    case 'RESET_ALL':
      return {
        ...initialState,
        homeTeamName: state.homeTeamName,
        awayTeamName: state.awayTeamName
      };
    
    // Clear events only (for reset button)
    case 'CLEAR_EVENTS_STORAGE':
      // This action only clears events when user explicitly requests it
      localStorage.removeItem('rugby_events');
      return {
        ...state,
        events: []
      };
      
    // Load persisted state
    case 'LOAD_STATE':
      return { ...state, ...action.payload };
      
    // Set persisted flag
    case 'SET_PERSISTED':
      return { ...state, isPersisted: action.payload };
      
    default:
      return state;
  }
}

// Context provider
export function RugbyTrackerProvider({ children }) {
  const { user } = useAuth();
  const { createMatch, updateMatch, createEvent, deleteEvent, updateEvent } = useDatabase();
  const [state, dispatch] = useReducer(rugbyReducer, initialState);
  const [isLoadedFromStorage, setIsLoadedFromStorage] = useState(false); // Track initial load
  
  // Use the useTimer hook to handle timer state
  const { 
    seconds, 
    isRunning, 
    formattedTime, 
    startTimer, 
    pauseTimer, 
    resetTimer, 
    setTime 
  } = useTimer({
    // Pass these options to maintain localStorage persistence
    initialSeconds: 0,
    onTick: (currentSeconds) => {
      // Update context state with current seconds from timer
      dispatch({ type: 'SET_SECONDS', payload: currentSeconds });
    }
  });
  
  // Keep timer running state in sync with context
  useEffect(() => {
    dispatch({ type: 'SET_TIMER_RUNNING', payload: isRunning });
  }, [isRunning]);
  
  // Load state from localStorage on mount (fallback for offline)
  useEffect(() => {
    console.log("Attempting to load state from localStorage...");
    let stateLoaded = false;
    try {
      const storedEvents = localStorage.getItem('rugby_events');
      let loadedEvents = []; // Default to empty array

      // --- Try loading events ---
      if (storedEvents) {
        try {
          const parsedEvents = JSON.parse(storedEvents);
          if (Array.isArray(parsedEvents)) { // Check if it's an array
             loadedEvents = parsedEvents; // Assign parsed events
             console.log(`Found ${loadedEvents.length} events in localStorage.`);
          } else {
             console.log("Stored events found but not an array. Using empty array.");
          }
        } catch (parseError) {
          console.error('Error parsing stored events:', parseError);
          // Optionally clear corrupted data: localStorage.removeItem('rugby_events');
        }
      } else {
         console.log("No stored events found in localStorage.");
      }

      // --- Load other state items ---
      const loadedState = { events: loadedEvents }; // Initialize with loaded/default events
      const storedHomeTeam = localStorage.getItem('rugby_home_team_name');
      const storedAwayTeam = localStorage.getItem('rugby_away_team_name');
      const storedSanctions = localStorage.getItem('rugby_sanctions');
      const storedEventSanctionMap = localStorage.getItem('rugby_event_sanction_map');
      const storedDirection = localStorage.getItem('rugby_team_direction');
      // IMPORTANT: Also load currentMatchId and isPersisted if they exist,
      // as these are crucial for the save logic.
      const storedMatchId = localStorage.getItem('rugby_current_match_id');
      const storedIsPersisted = localStorage.getItem('rugby_is_persisted');


      if (storedHomeTeam) { try { loadedState.homeTeamName = JSON.parse(storedHomeTeam); } catch(e) { console.error("Error parsing storedHomeTeam", e); } }
      if (storedAwayTeam) { try { loadedState.awayTeamName = JSON.parse(storedAwayTeam); } catch(e) { console.error("Error parsing storedAwayTeam", e); } }
      if (storedSanctions) { try { loadedState.sanctions = JSON.parse(storedSanctions); } catch(e) { console.error("Error parsing storedSanctions", e); } }
      if (storedEventSanctionMap) { try { loadedState.eventSanctionMap = JSON.parse(storedEventSanctionMap); } catch(e) { console.error("Error parsing storedEventSanctionMap", e); } }
      if (storedDirection) { try { loadedState.teamDirection = JSON.parse(storedDirection); } catch(e) { console.error("Error parsing storedDirection", e); } }
      // Load match ID and persisted flag if they exist in localStorage
      if (storedMatchId) {
          try { loadedState.currentMatchId = JSON.parse(storedMatchId); } catch (e) { console.error("Error parsing storedMatchId", e); }
      }
      if (storedIsPersisted) {
          try { loadedState.isPersisted = JSON.parse(storedIsPersisted); } catch (e) { console.error("Error parsing storedIsPersisted", e); }
      }


      // Dispatch combined state if anything was loaded (events always included)
      // Check if loadedState has more keys than just 'events', or if events array is not empty
      if (Object.keys(loadedState).length > 1 || loadedState.events.length > 0) {
         console.log("Dispatching combined LOAD_STATE:", loadedState);
         dispatch({ type: 'LOAD_STATE', payload: loadedState });
         stateLoaded = true;
      }

      if (stateLoaded) {
         console.log("Finished loading state from localStorage.");
      } else {
         console.log("No state found in localStorage to load (or only empty events array).");
      }

    } catch (error) {
      console.error('Error loading state from localStorage:', error);
    } finally {
       // Ensure this runs even if there's an error during loading
       console.log("Setting isLoadedFromStorage to true.");
       setIsLoadedFromStorage(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount
  
  // Persist state to localStorage when relevant parts change, AFTER initial load
  useEffect(() => {
    // Wait until initial load attempt is complete before persisting
    if (!isLoadedFromStorage) {
      // console.log("Skipping persistence: Initial load not complete."); // Optional log
      return;
    }
    
    // Persist the current state directly. Ensure defaults for potentially null/undefined values.
    // console.log("Persisting state to localStorage:", state); // Optional: Log full state being persisted

    localStorage.setItem('rugby_events', JSON.stringify(state.events || []));
    localStorage.setItem('rugby_home_team_name', JSON.stringify(state.homeTeamName || 'Home'));
    localStorage.setItem('rugby_away_team_name', JSON.stringify(state.awayTeamName || 'Away'));
    localStorage.setItem('rugby_sanctions', JSON.stringify(state.sanctions || []));
    localStorage.setItem('rugby_event_sanction_map', JSON.stringify(state.eventSanctionMap || {}));
    localStorage.setItem('rugby_team_direction', JSON.stringify(state.teamDirection || 'left-to-right'));
    // IMPORTANT: Also persist currentMatchId and isPersisted
    localStorage.setItem('rugby_current_match_id', JSON.stringify(state.currentMatchId || null));
    localStorage.setItem('rugby_is_persisted', JSON.stringify(state.isPersisted || false));

  }, [
       state.homeTeamName, state.awayTeamName, state.events, state.sanctions,
       state.eventSanctionMap, state.teamDirection, state.currentMatchId, state.isPersisted,
       isLoadedFromStorage // Added dependency as required by ESLint
     ]);
  
  // Update scores whenever events change
  useEffect(() => {
    const calculateScores = () => {
      const { events, currentMatchId, isPersisted } = state;
      let homeScore = 0;
      let awayScore = 0;
      
      events.forEach(event => {
        const scoreValue = SCORE_EVENTS[event.eventType];
        if (scoreValue) {
          if (event.team === 'Home') {
            homeScore += scoreValue;
          } else {
            awayScore += scoreValue;
          }
        }
      });
    
      dispatch({ 
        type: 'UPDATE_SCORES', 
        payload: { homeTeamScore: homeScore, awayTeamScore: awayScore } 
      });
      
      // If there's a current match in Supabase, update the scores
      if (currentMatchId && isPersisted) {
        updateMatch(currentMatchId, {
          homeTeamScore: homeScore,
          awayTeamScore: awayScore
        }).catch(console.error);
      }
    };
    
    calculateScores();


  // Not including the full state object to avoid render loops
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.events, state.currentMatchId, state.isPersisted, updateMatch]);
  
  // Check for expired sanctions when time changes
  useEffect(() => {
    dispatch({ type: 'CHECK_SANCTIONS' });
  }, [seconds]); // Use seconds from useTimer
  
  // Create custom action dispatchers for components to use
  const actions = {
    // Timer actions - now managed by useTimer
    startTimer,
    pauseTimer,
    resetTimer,
    setSeconds: setTime, // Map setTime from useTimer to setSeconds for compatibility
    
    // Team actions
    setHomeTeamName: (name) => dispatch({ type: 'SET_HOME_TEAM_NAME', payload: name }),
    setAwayTeamName: (name) => dispatch({ type: 'SET_AWAY_TEAM_NAME', payload: name }),
    setTeamDirection: (direction) => dispatch({ type: 'SET_TEAM_DIRECTION', payload: direction }),
    
    // Period actions
    setMatchPeriod: (period) => dispatch({ type: 'SET_MATCH_PERIOD', payload: period }),
    
    // Event form actions
    setEventType: (type) => dispatch({ type: 'SET_EVENT_TYPE', payload: type }),
    setTeam: (team) => dispatch({ type: 'SET_TEAM', payload: team }),
    setComment: (comment) => dispatch({ type: 'SET_COMMENT', payload: comment }),
    
    // Event actions with Supabase integration
    addEvent: async (event) => {
      dispatch({ type: 'ADD_EVENT', payload: event });
      
      // Sync to Supabase if we have a current match
      if (state.currentMatchId && user) {
        try {
          await createEvent(state.currentMatchId, event);
        } catch (error) {
          console.error('Error syncing event to Supabase:', error);
        }
      }
      
      // Check if this is a card event
      if (event.eventType === 'Yellow Card' || event.eventType === 'Red Card') {
        // Create sanction
        const sanctionId = Date.now();
        const duration = event.eventType === 'Yellow Card' ? 600 : 1200; // 10 or 20 mins
        
        const sanction = {
          id: sanctionId,
          eventId: event.id,
          team: event.team,
          cardType: event.eventType.split(' ')[0], // 'Yellow' or 'Red'
          startTime: event.seconds,
          duration,
          isActive: true
        };
        
        dispatch({ type: 'ADD_SANCTION', payload: sanction });
      }
    },
    
    deleteEvent: async (eventId) => {
      // Check if this event has an associated sanction
      const sanctionId = state.eventSanctionMap[eventId];
      
      if (sanctionId) {
        console.log(`Removing sanction ${sanctionId} associated with deleted event ${eventId}`);
        
        // If there's an associated sanction, completely remove it
        dispatch({
          type: 'REMOVE_SANCTION',
          payload: sanctionId
        });
        
        // Create a new eventSanctionMap without this entry
        const updatedEventSanctionMap = { ...state.eventSanctionMap };
        delete updatedEventSanctionMap[eventId];
        
        // Update the eventSanctionMap in state
        dispatch({
          type: 'LOAD_STATE',
          payload: { eventSanctionMap: updatedEventSanctionMap }
        });
      }
      
      // Delete the event
      dispatch({ type: 'DELETE_EVENT', payload: eventId });
      
      // Sync to Supabase if we have a current match
      if (state.currentMatchId && user) {
        try {
          await deleteEvent(eventId);
        } catch (error) {
          console.error('Error deleting event from Supabase:', error);
        }
      }
    },
    
    editEvent: async (event) => {
      dispatch({ type: 'EDIT_EVENT', payload: event });
      
      // Sync to Supabase if we have a current match
      if (state.currentMatchId && user) {
        try {
          await updateEvent(event.id, event);
        } catch (error) {
          console.error('Error updating event in Supabase:', error);
        }
      }
    },
    
    clearEvents: () => dispatch({ type: 'CLEAR_EVENTS' }),
    
    // Sanction actions
    updateSanction: (sanction) => dispatch({ type: 'UPDATE_SANCTION', payload: sanction }),
    completeSanction: (sanctionId) => dispatch({ type: 'COMPLETE_SANCTION', payload: sanctionId }),
    clearSanctions: () => dispatch({ type: 'CLEAR_SANCTIONS' }),
    
    // Supabase integration actions
    saveMatch: async () => {
      // Check if Supabase integration is available - if not, fall back to localStorage only
      if (!user) {
        console.log('No authenticated user found. Saving to localStorage only.');
        // Store a flag or message indicating the match is saved locally only
        localStorage.setItem('rugby_match_saved_locally', JSON.stringify({
          timestamp: new Date().toISOString(),
          message: 'Match saved locally. Log in to save to the cloud.'
        }));
        
        return 'local-storage-only';
      }
      
      try {
        const matchData = {
          matchName: `${state.homeTeamName} vs ${state.awayTeamName}`,
          homeTeamName: state.homeTeamName,
          awayTeamName: state.awayTeamName,
          homeTeamScore: state.homeTeamScore,
          awayTeamScore: state.awayTeamScore,
          events: state.events,
          periods: {
            firstHalf: 2400, // 40 mins in seconds
            secondHalf: 2400,
            overtime: 0
          },
          timestamp: new Date().toISOString()
        };
        
        let matchId;
        
        if (state.currentMatchId) {
          // Update existing match
          const updatedMatch = await updateMatch(state.currentMatchId, matchData);
          matchId = updatedMatch.id;
        } else {
          // Create new match
          const newMatch = await createMatch(matchData);
          matchId = newMatch.id;
          
          
          // Update current match ID and preserve current state details
          dispatch({
            type: 'SET_CURRENT_MATCH',
            payload: {
              id: newMatch.id, // Get ID from the newly created match
              homeTeamName: state.homeTeamName, // Keep current state name
              awayTeamName: state.awayTeamName, // Keep current state name
              homeTeamScore: state.homeTeamScore, // Keep current state score
              awayTeamScore: state.awayTeamScore, // Keep current state score
              events: state.events // Keep current events
            }
          });
        }
        
        dispatch({ type: 'SET_PERSISTED', payload: true });
        return matchId;
      } catch (error) {
        console.error('Error saving match to Supabase:', error);
        throw error;
      }
    },
    
    loadMatch: (match) => {
      if (!match) return;
      
      // Convert Supabase events to local format if needed
      const formattedEvents = match.events?.map(event => ({
        id: event.id,
        eventType: event.event_type,
        team: event.team,
        playerNumber: event.player_number,
        timestamp: event.timestamp,
        seconds: event.seconds,
        period: event.period,
        position: {
          x: event.position_x,
          y: event.position_y
        },
        comment: event.comment,
        zone: event.zone
      })) || [];
      
      dispatch({
        type: 'SET_CURRENT_MATCH',
        payload: {
          id: match.id,
          homeTeamName: match.home_team?.name || match.home_team_name,
          awayTeamName: match.away_team?.name || match.away_team_name,
          homeTeamScore: match.home_team_score,
          awayTeamScore: match.away_team_score,
          events: formattedEvents
        }
      });
    },
    
    // Reset all - need to also reset timer
    resetAll: () => {
      // First clear events from localStorage explicitly
      dispatch({ type: 'CLEAR_EVENTS_STORAGE' });
      // Then reset the state
      dispatch({ type: 'RESET_ALL' });
      dispatch({ type: 'SET_PERSISTED', payload: false });
      resetTimer(); // Use resetTimer from useTimer
    }
  };
  
  // Calculate active sanctions
  const activeSanctions = state.sanctions.filter(s => s.isActive);
  
  // Get remaining time for a sanction
  const getRemainingTime = (sanction) => {
    const elapsedSeconds = seconds - sanction.startTime; // Use seconds from useTimer
    return Math.max(0, sanction.duration - elapsedSeconds);
  };
  
  // Format remaining time for display
  const getFormattedRemainingTime = (sanction) => {
    return formatTime(getRemainingTime(sanction));
  };
  
  // Check if sanction is in warning state (last minute)
  const isInWarningState = (sanction) => {
    const remaining = getRemainingTime(sanction);
    return remaining > 0 && remaining <= 60; // Last minute warning
  };
  
  // Enhanced state with computed values
  const enhancedState = {
    ...state,
    seconds, // Use seconds from useTimer
    isRunning, // Use isRunning from useTimer
    formattedTime, // Use formattedTime from useTimer
    activeSanctions,
    EVENT_TYPES
  };
  
  // Enhanced actions with sanction utilities
  const enhancedActions = {
    ...actions,
    getRemainingTime,
    getFormattedRemainingTime,
    isInWarningState
  };
  
  return (
    <RugbyTrackerContext.Provider value={{ state: enhancedState, actions: enhancedActions }}>
      {children}
    </RugbyTrackerContext.Provider>
  );
}

// Custom hook for using the context
export function useRugbyTracker() {
  const context = useContext(RugbyTrackerContext);
  if (!context) {
    throw new Error('useRugbyTracker must be used within a RugbyTrackerProvider');
  }
  return context;
} 